import { useEffect, useMemo, useRef, useState } from "react";
import type { Indicador, IndicadorTableConfig, IndicadorDataRow } from "../types/indicadores";

const MONTH_NUMBERS: Record<string, string> = {
  enero: "01",
  febrero: "02",
  marzo: "03",
  abril: "04",
  mayo: "05",
  junio: "06",
  julio: "07",
  agosto: "08",
  septiembre: "09",
  octubre: "10",
  noviembre: "11",
  diciembre: "12",
};

type Props = {
  indicator: Indicador;
  config: IndicadorTableConfig;
  rows: IndicadorDataRow[];
  loading?: boolean;
  placeholderCount?: number;
};

type ChartDefinition = {
  title: string;
  values: Array<Record<string, string | number>>;
};

export function IndicadorChart({
  indicator,
  config,
  rows,
  loading = false,
  placeholderCount = 1,
}: Props) {
  const charts = useMemo(
    () => buildCharts(indicator, config, rows),
    [config, indicator, rows],
  );

  if (!config.chart && !loading) {
    return null;
  }

  if (loading) {
    return (
      <section className="indicadores-charts-grid" aria-label="Cargando gráficos" aria-busy="true">
        {Array.from({ length: Math.max(1, placeholderCount) }, (_, index) => (
          <article className="indicadores-chart-card" key={`chart-loading-${index}`}>
            <h3>{index === 0 ? "Gráfico del indicador" : "Gráfico complementario"}</h3>
            <div className="indicadores-chart-loading-body">
              <div className="indicadores-skeleton indicadores-skeleton-chart" aria-hidden="true" />
            </div>
          </article>
        ))}
      </section>
    );
  }

  return (
    <section className="indicadores-charts-grid" aria-label={`Gráficos de ${indicator.nombre}`}>
      {charts.map((chart) => (
        <ChartPanel key={chart.title} chart={chart} />
      ))}
    </section>
  );
}

function ChartPanel({ chart }: { chart: ChartDefinition }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const hasValues = chart.values.length > 0;

  useEffect(() => {
    const element = ref.current;

    if (!element) {
      return undefined;
    }

    const measure = () => {
      const styles = window.getComputedStyle(element);
      const horizontalPadding = Number.parseFloat(styles.paddingLeft)
        + Number.parseFloat(styles.paddingRight);
      const width = Math.max(0, element.clientWidth - horizontalPadding);

      setContainerWidth((current) => (
        Math.abs(current - width) >= 4 ? Math.floor(width) : current
      ));
    };

    measure();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }

    const observer = new ResizeObserver(() => measure());
    observer.observe(element);

    return () => observer.disconnect();
  }, [hasValues]);

  useEffect(() => {
    let disposed = false;
    let view: { finalize?: () => void } | null = null;
    const element = ref.current;

    if (!element || !hasValues || containerWidth <= 0) {
      return undefined;
    }

    setError(null);
    element.innerHTML = "";

    const plotWidth = Math.max(260, containerWidth);
    const plotHeight = Math.max(320, Math.min(500, Math.round(plotWidth * 0.43)));

    void import("vega-embed")
      .then(({ default: embed }) =>
        embed(
          element,
          ({
            $schema: "https://vega.github.io/schema/vega-lite/v5.json",
            width: plotWidth,
            height: plotHeight,
            autosize: { type: "fit-x", contains: "padding", resize: true },
            padding: { left: 10, right: 10, top: 10, bottom: 10 },
            data: { values: chart.values },
            mark: {
              type: "line",
              point: { filled: true, size: 50 },
              strokeWidth: 2.4,
              tooltip: true,
            },
            encoding: {
              x: {
                field: "periodo",
                type: "ordinal",
                sort: { field: "orden", order: "ascending" },
                title: "Periodo",
                axis: {
                  labelAngle: -32,
                  labelLimit: 180,
                  labelOverlap: "greedy",
                  titlePadding: 14,
                },
              },
              y: {
                field: "valor",
                type: "quantitative",
                title: "Valor",
                axis: { format: ".4~g", titlePadding: 14 },
                scale: { zero: false },
              },
              color: {
                field: "serie",
                type: "nominal",
                title: "Serie",
                scale: {
                  range: ["#0b4a2f", "#0f766e", "#00a879", "#22c55e", "#65a30d", "#0891b2"],
                },
                legend: {
                  orient: "bottom",
                  direction: "horizontal",
                  columns: 3,
                  labelLimit: 220,
                  symbolLimit: 220,
                  titlePadding: 8,
                },
              },
              detail: { field: "serie" },
              order: { field: "orden", type: "quantitative" },
              tooltip: [
                { field: "periodo", type: "nominal", title: "Periodo" },
                { field: "serie", type: "nominal", title: "Serie" },
                { field: "valor", type: "quantitative", title: "Valor", format: ".6~g" },
              ],
            },
            config: {
              background: "transparent",
              view: { stroke: null },
              axis: {
                gridColor: "#e2e8f0",
                gridOpacity: 0.72,
                domainColor: "#94a3b8",
                labelColor: "#475569",
                titleColor: "#334155",
                labelFontSize: 11,
                titleFontSize: 12,
              },
              legend: {
                labelColor: "#475569",
                titleColor: "#334155",
                labelFontSize: 11,
                titleFontSize: 12,
              },
            },
          } as any),
          { actions: false, renderer: "svg" },
        ),
      )
      .then((result) => {
        if (disposed) {
          result.view.finalize();
          return;
        }
        view = result.view;
      })
      .catch(() => {
        if (!disposed) {
          setError("No se pudo representar el gráfico.");
        }
      });

    return () => {
      disposed = true;
      view?.finalize?.();
      element.innerHTML = "";
    };
  }, [chart, containerWidth, hasValues]);

  return (
    <article className="indicadores-chart-card">
      <h3>{chart.title}</h3>
      {!hasValues ? (
        <div className="indicadores-empty-state indicadores-chart-empty">
          No hay valores disponibles para representar.
        </div>
      ) : error ? (
        <div className="indicadores-inline-error">{error}</div>
      ) : (
        <div ref={ref} className="indicadores-chart" aria-busy={containerWidth === 0} />
      )}
    </article>
  );
}

function buildCharts(
  indicator: Indicador,
  config: IndicadorTableConfig,
  rows: IndicadorDataRow[],
): ChartDefinition[] {
  if (!config.chart) {
    return [];
  }

  if (config.kind === "electric") {
    const seriesThree = ["kwh_m2", "kwh_hdd", "kwh_cdd"];
    const seriesFour = [...seriesThree, "kwh_hdd_cdd"];

    return [
      {
        title: "Consumo relativo: Kwh/m², Kwh/Hdd y Kwh/Cdd",
        values: buildElectricValues(rows, seriesThree),
      },
      {
        title: "Consumo relativo incluyendo Kwh/HDD+CDD",
        values: buildElectricValues(rows, seriesFour),
      },
    ];
  }

  return [
    {
      title: `Evolución de ${indicator.nombre}`,
      values: buildMonthlyValues(config, rows),
    },
  ];
}

function buildMonthlyValues(
  config: IndicadorTableConfig,
  rows: IndicadorDataRow[],
): Array<Record<string, string | number>> {
  const values: Array<Record<string, string | number>> = [];

  rows.forEach((row) => {
    config.months.forEach((month, monthIndex) => {
      const raw = parseChartValue(row[month]);
      if (raw === null) {
        return;
      }

      const year = Number(row.anio);
      values.push({
        periodo: `${year}-${MONTH_NUMBERS[month] ?? String(monthIndex + 1).padStart(2, "0")}-01`,
        orden: year * 100 + monthIndex + 1,
        serie: String(row.indicador || "Valor"),
        valor: raw,
      });
    });
  });

  return values;
}

function buildElectricValues(
  rows: IndicadorDataRow[],
  fields: string[],
): Array<Record<string, string | number>> {
  const labels: Record<string, string> = {
    kwh_m2: "Kwh/m²",
    kwh_hdd: "Kwh/Hdd",
    kwh_cdd: "Kwh/Cdd",
    kwh_hdd_cdd: "Kwh/HDD+CDD",
  };
  const values: Array<Record<string, string | number>> = [];

  rows.forEach((row, index) => {
    const period = [
      row.anio,
      row.inicio_periodo_facturacion,
      row.fin_periodo_facturacion,
    ]
      .filter((value) => value !== null && value !== "")
      .join(" · ");

    fields.forEach((field) => {
      const raw = parseChartValue(row[field]);
      if (raw === null) {
        return;
      }

      values.push({
        periodo: period || `Registro ${index + 1}`,
        orden: index,
        serie: labels[field] ?? field,
        valor: raw,
      });
    });
  });

  return values;
}

function parseChartValue(value: string | number | null | undefined): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== "string") {
    return null;
  }

  const normalized = value.trim();
  if (normalized === "") {
    return null;
  }

  const isPercentage = normalized.endsWith("%");
  const valueWithoutPercentage = normalized
    .replace(/%$/, "")
    .replace(/\s/g, "");
  const numericText = valueWithoutPercentage.includes(",")
    ? valueWithoutPercentage.replace(/\./g, "").replace(",", ".")
    : valueWithoutPercentage;
  const parsed = Number(numericText);

  if (!Number.isFinite(parsed)) {
    return null;
  }

  return isPercentage ? parsed / 100 : parsed;
}
